import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import GridAreaContainer from '@sprinx/react-mui-components/GridAreaContainer';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { currencyState } from '../../../api/appState';
import { CatalogueProduct } from '../../../api/products/catalogue';
import ProductDiscountPercentElement from '../../../components/ProductDiscountPercentElement';
import ProductPriceElement from '../../../components/ProductPriceElement';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface CataloguePageListContentItemRowPriceProps
  extends StandardDivProps<CataloguePageListContentItemRowPriceClassKey> {
  product: CatalogueProduct;
}

export type CataloguePageListContentItemRowPriceClassKey = 'root' | 'wrapper' | 'discounts';

const themeSettings = { name: 'CataloguePageListContentItemRowPrice' };
const useStyles = makeStyles(
  (/* theme */) =>
    createStyles<CataloguePageListContentItemRowPriceClassKey, {}>({
      root: {},
      wrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      },
      discounts: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
      },
    }),
  themeSettings,
);

const CataloguePageListContentItemRowPrice = React.forwardRef<
  HTMLDivElement,
  CataloguePageListContentItemRowPriceProps
>(({ className, classes: pClasses, product, ...rest }, ref) => {
  const classes = useStyles({ classes: pClasses });
  const currency = useRecoilValue(currencyState);
  const t = useTranslateWithFallback();

  const cu3Price = (product.extra?.cu3Price || []).find(
    (p) => p.currency === currency,
  )?.amount as number | undefined;

  const price = product.price as unknown as number;

  const hasDifferentPrices =
    cu3Price !== undefined && (cu3Price as number) !== price;

  const primaryPrice = hasDifferentPrices ? price : (cu3Price ?? price);

  const secondaryPrice =
    hasDifferentPrices && cu3Price !== undefined && cu3Price >= primaryPrice
      ? cu3Price
      : null;

  return (
    <GridAreaContainer ref={ref} className={clsx(classes.root, className)} renderToGridArea='price' {...rest}>
      <div className={classes.wrapper}>
        <div className={classes.discounts}>
          {/* <AbsoluteDiscount value={product.discountAbsolute} currency={currency} /> */}
          <PercentDiscount value={product.discountPercent} />
        </div>
        <PrimaryPrice value={primaryPrice} currency={currency} />
        {!!secondaryPrice && (product?.taxInfo?.value ?? 0) > 0 && (
          <SecondaryPrice value={secondaryPrice} currency={currency} title={t('common/cu3Price')} />
        )}
      </div>
    </GridAreaContainer>
  );
});

CataloguePageListContentItemRowPrice.displayName = 'CataloguePageListContentItemRowPrice';

export default withThemeProps(themeSettings)(CataloguePageListContentItemRowPrice);

const PrimaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.h6,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  label: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },
}))(ProductPriceElement) as typeof ProductPriceElement;

const SecondaryPrice = withStyles((theme) => ({
  root: {
    ...theme.typography.body1,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  label: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
  },
}))(ProductPriceElement) as typeof ProductPriceElement;

const elementStyles = (absolute?: boolean) => (theme: Theme) => ({
  root: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.primary.main,
    ...(absolute ? { textDecoration: 'line-through' } : {}),
  },
});

// const AbsoluteDiscount = withStyles(elementStyles(true))(ProductDiscountAbsoluteElement);
const PercentDiscount = withStyles(elementStyles(false))(
  ProductDiscountPercentElement,
) as typeof ProductDiscountPercentElement;
